import { AddressInput } from '@/components/modules/Input/AddressInput';
import type { CreateSiteData, Position, Site } from '@/types/site';
import { useState } from 'react';
import type { SiteAddTabProps, SiteEditTabProps } from './types';

type LocationInfoTabProps = SiteEditTabProps | SiteAddTabProps;

export default function LocationInfoTab({
  site,
  onSiteChange: handleSiteChange,
}: LocationInfoTabProps) {
  // 타입 안전한 사이트 변경 핸들러
  const handleSiteChangeTyped = (updatedSite: Site | CreateSiteData) => {
    // 타입 단언: SiteEditTabProps와 SiteAddTabProps의 유니온 타입 처리
    (handleSiteChange as (site: Site | CreateSiteData) => void)(updatedSite);
  };

  const [waterSupplyMapStates, setWaterSupplyMapStates] = useState<boolean[]>(
    []
  );

  // Position 업데이트 헬퍼 함수
  const updatePosition = (
    positionType: 'well' | 'management',
    updates: Partial<Position>
  ) => {
    if (positionType === 'well') {
      handleSiteChangeTyped({
        ...site,
        wellPosition: {
          ...site.wellPosition,
          ...updates,
        },
      });
    } else if (positionType === 'management') {
      handleSiteChangeTyped({
        ...site,
        managementPosition: {
          address: '',
          latitude: 0,
          longitude: 0,
          level: 0,
          name: '',
          ...site.managementPosition,
          ...updates,
        },
      });
    }
  };

  // 급수 위치 업데이트
  const updateWaterSupplyPosition = (
    index: number,
    updates: Partial<Position>
  ) => {
    const positions = site.waterSupplyPosition || [];
    const newPositions = [...positions];
    newPositions[index] = {
      ...newPositions[index],
      ...updates,
    };
    handleSiteChangeTyped({
      ...site,
      waterSupplyPosition: newPositions,
    });
  };

  // 급수 위치 추가/제거
  const addWaterSupplyPosition = () => {
    const newPosition: Position = {
      address: '',
      latitude: 0,
      longitude: 0,
      level: 0,
      name: '',
    };
    handleSiteChangeTyped({
      ...site,
      waterSupplyPosition: [...(site.waterSupplyPosition || []), newPosition],
    });
    // 새로운 위치의 지도 상태 추가
    setWaterSupplyMapStates([...waterSupplyMapStates, false]);
  };

  const removeWaterSupplyPosition = (index: number) => {
    const positions = site.waterSupplyPosition || [];
    handleSiteChangeTyped({
      ...site,
      waterSupplyPosition: positions.filter((_, i) => i !== index),
    });
    // 해당 위치의 지도 상태 제거
    const newMapStates = waterSupplyMapStates.filter((_, i) => i !== index);
    setWaterSupplyMapStates(newMapStates);
  };

  return (
    <div className="space-y-8">
      {/* 관정 위치 (필수) */}
      <div className="bg-blue-50 p-4 rounded-lg">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          관정 위치 정보
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              관정명 <span className="text-red-500">(필수)</span>
            </label>
            <input
              type="text"
              value={site.wellPosition.name || ''}
              onChange={(e) => updatePosition('well', { name: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              수위 (레벨)
            </label>
            <input
              type="number"
              step="0.1"
              value={site.wellPosition.level || ''}
              onChange={(e) =>
                updatePosition('well', {
                  level: Number(e.target.value) || 0,
                })
              }
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              위도 <span className="text-red-500"></span>
            </label>
            <input
              type="number"
              step="0.000001"
              value={site.wellPosition.latitude || ''}
              onChange={(e) =>
                updatePosition('well', {
                  latitude: Number(e.target.value) || 0,
                })
              }
              disabled={true}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              경도 <span className="text-red-500"></span>
            </label>
            <input
              type="number"
              step="0.000001"
              disabled={true}
              value={site.wellPosition.longitude || ''}
              onChange={(e) =>
                updatePosition('well', {
                  longitude: Number(e.target.value) || 0,
                })
              }
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">
              관정 주소 <span className="text-red-500">(필수)</span>
            </span>
          </div>
          <AddressInput
            label=""
            onChange={(fullAddress, coordinates) =>
              updatePosition('well', {
                address: fullAddress,
                latitude: coordinates?.latitude || site.wellPosition.latitude,
                longitude:
                  coordinates?.longitude || site.wellPosition.longitude,
              })
            }
            initialMapPosition={{
              latitude: site.wellPosition.latitude,
              longitude: site.wellPosition.longitude,
              address: site.wellPosition.address,
              name: site.wellPosition.name,
            }}
            className="w-full"
            isMapOpen={true}
          />
        </div>
      </div>

      {/* 관리 위치 */}
      {/* <div className="bg-green-50 p-4 rounded-lg">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          관리 위치 정보
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              관리소명 <span className="text-red-500">(필수)</span>
            </label>
            <input
              type="text"
              value={site.managementPosition?.name || ''}
              onChange={(e) =>
                updatePosition('management', { name: e.target.value })
              }
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              레벨
            </label>
            <input
              type="number"
              step="0.1"
              value={site.managementPosition?.level || ''}
              onChange={(e) =>
                updatePosition('management', {
                  level: Number(e.target.value) || 0,
                })
              }
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              위도
            </label>
            <input
              type="number"
              step="0.000001"
              disabled={true}
              value={site.managementPosition?.latitude || ''}
              onChange={(e) =>
                updatePosition('management', {
                  latitude: Number(e.target.value) || 0,
                })
              }
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              경도
            </label>
            <input
              type="number"
              step="0.000001"
              disabled={true}
              value={site.managementPosition?.longitude || ''}
              onChange={(e) =>
                updatePosition('management', {
                  longitude: Number(e.target.value) || 0,
                })
              }
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">
              관리소 주소 <span className="text-red-500">(필수)</span>
            </span>
          </div>
          <AddressInput
            label=""
            onChange={(fullAddress, coordinates) =>
              updatePosition('management', {
                address: fullAddress,
                latitude:
                  coordinates?.latitude ||
                  site.managementPosition?.latitude ||
                  0,
                longitude:
                  coordinates?.longitude ||
                  site.managementPosition?.longitude ||
                  0,
              })
            }
            className="w-full"
            isMapOpen={true}
          />
        </div>
      </div> */}

      {/* 급수 위치들 */}
      <div className="bg-yellow-50 p-4 rounded-lg">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">급수 위치 정보</h3>
          <button
            onClick={addWaterSupplyPosition}
            className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm"
          >
            위치 추가
          </button>
        </div>
        {site.waterSupplyPosition?.map((position, index) => (
          <div key={index} className="bg-white p-4 rounded-lg mb-4 border">
            <div className="flex justify-between items-center mb-2">
              <h4 className="font-medium">급수 위치 {index + 1}</h4>
              <button
                onClick={() => removeWaterSupplyPosition(index)}
                className="text-red-600 hover:text-red-800 text-sm"
              >
                삭제
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  위치명
                </label>
                <input
                  type="text"
                  value={position.name || ''}
                  onChange={(e) =>
                    updateWaterSupplyPosition(index, {
                      name: e.target.value,
                    })
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  레벨
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={position.level || ''}
                  onChange={(e) =>
                    updateWaterSupplyPosition(index, {
                      level: Number(e.target.value) || 0,
                    })
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  위도
                </label>
                <input
                  type="number"
                  step="0.000001"
                  disabled={true}
                  value={position.latitude || ''}
                  onChange={(e) =>
                    updateWaterSupplyPosition(index, {
                      latitude: Number(e.target.value) || 0,
                    })
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  경도
                </label>
                <input
                  type="number"
                  step="0.000001"
                  disabled={true}
                  value={position.longitude || ''}
                  onChange={(e) =>
                    updateWaterSupplyPosition(index, {
                      longitude: Number(e.target.value) || 0,
                    })
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">
                  급수 위치 주소
                </span>
              </div>
              <AddressInput
                label=""
                initialMapPosition={{
                  latitude: position.latitude,
                  longitude: position.longitude,
                  address: position.address,
                  name: position.name,
                }}
                onChange={(fullAddress, coordinates) =>
                  updateWaterSupplyPosition(index, {
                    address: fullAddress,
                    latitude: coordinates?.latitude || 0,
                    longitude: coordinates?.longitude || 0,
                  })
                }
                className="w-full"
                isMapOpen={waterSupplyMapStates[index] || true}
              />
            </div>
          </div>
        )) || (
          <p className="text-gray-500 text-center py-4">
            등록된 급수 위치가 없습니다.
          </p>
        )}
      </div>
    </div>
  );
}
